import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { BusinessAccountRequestFactory } from './business-account-request.factory';
import { AccountGrid, AdminCompanyDetails, ApproveBusinessAdminPayload, BusinessAccountRequestLabel, BusinessAccountRequestState, BusinessAccountsGridData, EditAdminCompanyDetailsAemLabels, ModelButtons, RejectBusinessAccountPayload, UploadSuccessResponse } from './definitions';
import { ApiService } from '../../services/api/api.service';
import { GridValue, HeaderObj, AprRejUserData, ModalState, KngUploadFileState, KngUploadFileFactory, ErrorMessageSubHeading, ErrorMessageHeading } from 'kinetic-angular';
import { CssClass, TranslationKeys } from '../../services/shared/shared-enums';
import { SharedService } from '../../services/shared/shared.service';
import { HttpErrorResponse } from '@angular/common/http';
import { LoggerService } from '../../services/logger/logger.service';
import { SharedUtilService } from '../../services/shared/shared-util.service';
import { ToastrService } from 'ngx-toastr';
import { ApiResponse } from '../../definitions/api-response.interface';
import { FormGroup } from '@angular/forms';
import { BusinessAccountRequestUtilService } from './business-account-request-util.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
	providedIn: 'root'
})
export class BusinessAccountRequestService 
{
	private state$: BehaviorSubject<BusinessAccountRequestState> = new BehaviorSubject<BusinessAccountRequestState>(BusinessAccountRequestFactory.build());
	private editAdminCompanyDetailsAemLabelsState$: BehaviorSubject<EditAdminCompanyDetailsAemLabels> = new BehaviorSubject<EditAdminCompanyDetailsAemLabels>(BusinessAccountRequestFactory.buildEditAdminCompanyDetails());
	get editAdminCompanyDetailsAemLabelsState(): EditAdminCompanyDetailsAemLabels 
	{
		return this.editAdminCompanyDetailsAemLabelsState$.value;
	}
	get state(): BusinessAccountRequestState 
	{
		return this.state$.value;
	}
	get stateChanged(): Observable<BusinessAccountRequestState> 
	{
		return this.state$.asObservable();
	}
	editAdminCompanyDetailsModalState: ModalState = BusinessAccountRequestFactory.buildEditAdminCompanyDetailsModalState();
	businessAccountData: BusinessAccountsGridData[] = [];
	updateAdminDetailsErrorMessage: string = '';
	isAdminDetailsUpdated$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	uploadFileState: KngUploadFileState = KngUploadFileFactory.build();
	uploadModelState: ModalState = BusinessAccountRequestFactory.buildUploadModelState();
	uploadModelSubheading: string = '';
	modifiedFileName: string = '';
	tempUserId: string = '';
	modelButtons: ModelButtons = BusinessAccountRequestFactory.build().sectionMiddle.grid.uploadFileLabels.modelButtons;
	public buildEditDetailsForm: FormGroup = this.businessAccountUtilService.buildForm();

	constructor(private translate: TranslateService, public apiService: ApiService, private sharedService: SharedService, private loggerService: LoggerService, private sharedUtilService: SharedUtilService, private toaster: ToastrService, public businessAccountUtilService: BusinessAccountRequestUtilService) { }

	public updateState(value: BusinessAccountRequestState): void 
	{
		this.state$.next(value);
	}
	public updateEditAdminCompanyDetailsState(value: EditAdminCompanyDetailsAemLabels): void 
	{
		this.editAdminCompanyDetailsAemLabelsState$.next(value);
	}
	public getBusinessAccountRequestPageAemLabel() 
	{
		this.translate.get(TranslationKeys.businessAccountRequestPageLabels).subscribe({
			next: (res: BusinessAccountRequestLabel) => 
			{
				if (res) 
				{
					this.mapBusinessAccountRequestLabel(res);
					this.getBusinessAccountRequestGridData();
				}
			}
		});
	}
	public mapBusinessAccountRequestLabel(data: BusinessAccountRequestLabel) 
	{
		const sectionTop = this.state.sectionTop;
		const sectionMiddle = this.state.sectionMiddle;
		sectionTop.pageHeading = data.pageHeading;
		sectionMiddle.tableHeading = data.tableHeading;
		sectionMiddle.grid.companyGridLabel = data.grid.companyGridLabel;
		if (sectionMiddle.tableState.tooltip) 
			sectionMiddle.tableState.tooltip.content = data.grid.tooltip;
		sectionMiddle.tableState.noResult.image.altText = data.grid.noResult.altText ? data.grid.noResult.altText : '';
		sectionMiddle.tableState.noResult.text = data.grid.noResult.label ? data.grid.noResult.label : '';
		const headers: HeaderObj[] = [];
		data.grid.headers.forEach((header) => 
		{
			headers.push(this.businessAccountUtilService.updateHeaderObj(header.id, header.label));
		});
		this.state.sectionMiddle.tableState.gridHeaders = headers;
		this.modelButtons = data.grid.uploadFileLabels.modelButtons;
		this.uploadModelSubheading = data.grid.uploadFileLabels.subHeading;
		this.businessAccountUtilService.mapUploadFilePopup(data.grid.uploadFileLabels, this.uploadFileState);
		this.businessAccountUtilService.mapUploadModelState(data.grid.uploadFileLabels, this.uploadModelState);		
		this.resetUploadFileModelFlags();
	}
	resetUploadFileModelFlags()
	{
		this.businessAccountUtilService.resetUploadFileModelFlags(this.uploadFileState);
	}
	checkServerValidationUploadFile(item: File)
	{
		this.uploadFileState.isLoading = true;
		this.uploadModelState.buttons[0].disabled = true;
		this.uploadModelState.buttons[1].hidden = true;
		const ele = document.getElementById('kng-modal-close');
		if(ele)
			ele.setAttribute('disabled', 'true');
		this.apiService.getServerValidationUploadFile(item, this.tempUserId).subscribe({
			next: (res: ApiResponse) => 
			{
				if (res?.data)
				{
					const responseObject: UploadSuccessResponse = <UploadSuccessResponse>(res.data);
					this.modifiedFileName = responseObject.updatedFileName;
					this.uploadModelState.buttons[0].hidden = false;
					this.uploadModelState.buttons[1].hidden = true;
					this.uploadModelState.buttons[0].disabled = false;
					this.uploadModelState.buttons[0].label = this.modelButtons.close;
					this.uploadModelState.buttons[0].cssClass = CssClass.btnBlack;
					this.uploadFileState.isLoading = false;
					this.uploadFileState.isError = false;
					this.uploadFileState.isSuccessClient = true;
					this.uploadFileState.isSuccessServer = true;
					if(ele)
						ele.removeAttribute('disabled');
				}
			},
			error: (error: HttpErrorResponse): void => 
			{
				this.uploadModelState.buttons[0].disabled = false;
				this.uploadModelState.buttons[1].hidden = true;
				this.uploadFileState.isLoading = false;
				this.uploadFileState.isError = true;
				this.uploadFileState.isSuccessClient = true;
				this.uploadFileState.isSuccessServer = false;
				if(ele)
					ele.removeAttribute('disabled');
				if(error.error.statusCode === 400)
				{
					this.uploadFileState.isSuccessClient = false;
					this.uploadFileState.errorHeading = ErrorMessageHeading.wrongData;
					this.uploadFileState.errorSubHeading = ErrorMessageSubHeading.wrongData;
				}
				else if(error.error.statusCode === 403)
				{
					this.uploadFileState.errorHeading = ErrorMessageHeading.uploadFailed;
					this.uploadFileState.errorSubHeading = ErrorMessageSubHeading.apiFailure;
				}
			},
			complete: () => 
			{
				setTimeout(() => 
				{
					this.uploadFileState.isLoading = false;
				}, 1000);
				this.getBusinessAccountRequestGridData();
			}
		});
	}
	public getBusinessAccountRequestGridData() 
	{
		this.state.sectionMiddle.tableState.loading = true;
		this.apiService.getBusinessAccountRequestData().subscribe({
			next: (response) => 
			{
				this.state.sectionMiddle.isError = false;
				const data = response.data as BusinessAccountsGridData[];
				this.businessAccountData = data;
				if (data) 
					this.mapToTableState(data);
			},
			error: (error: HttpErrorResponse): void => 
			{
				if (error) 
				{
					this.state.sectionMiddle.isError = true;
					this.state.sectionMiddle.errorState= this.businessAccountUtilService.getErrorResponse();
				}
			},
			complete: () => 
			{
				setTimeout(() => 
				{
					this.state.sectionMiddle.tableState.loading = false;
				}, 1000);
			}
		});
	}
	mapToTableState(res: BusinessAccountsGridData[]) 
	{
		setTimeout(() => 
		{
			const rowValue: GridValue[] = [];
			const resources: BusinessAccountsGridData[] = res;
			this.state.sectionMiddle.businessAccountData = resources;
			resources.forEach((obj: BusinessAccountsGridData) => 
			{
				const valueObj: GridValue = this.businessAccountUtilService.setDefaultValues(obj);
				valueObj.values.push(this.businessAccountUtilService.updateAccessGridData(AccountGrid.requestedBy, obj, this.state));
				valueObj.values.push(this.businessAccountUtilService.updateAccessGridData(AccountGrid.requestedOn, obj, this.state));
				valueObj.values.push(this.businessAccountUtilService.updateAccessGridData(AccountGrid.topLevelGrid, obj, this.state));
				valueObj.values.push(this.businessAccountUtilService.updateAccessGridData(AccountGrid.adminCompanyDetails, obj, this.state));
				valueObj.values.push(this.businessAccountUtilService.updateAccessGridData(AccountGrid.contact, obj, this.state));
				valueObj.values.push(this.businessAccountUtilService.updateAccessGridData(AccountGrid.locationList, obj, this.state));
				valueObj.values.push(this.businessAccountUtilService.updateAccessGridData(AccountGrid.businessAccountApproval, obj, this.state));
				rowValue.push(valueObj);
			});
			this.state.sectionMiddle.tableState.gridData = rowValue;
		});
	}
	rejectUsers(usersList: AprRejUserData[]) 
	{
		this.state.sectionMiddle.loading = true;
		if (usersList?.length > 0) 
		{
			const rejectBusinessAccountPayload: RejectBusinessAccountPayload = { email: usersList[0].email };
			this.apiService.rejectBusinessAccount(rejectBusinessAccountPayload).subscribe({
				next: (res: ApiResponse) => 
				{
					if (res) 
					{
						this.state.sectionMiddle.loading = false;
						this.getBusinessAccountRequestGridData();
						this.toaster.success(res.message);
					}
				},
				error: (error: HttpErrorResponse): void => 
				{
					if (error) 
					{
						this.state.sectionMiddle.loading = false;
						this.loggerService.error(error.error);
						this.toaster.error(error.error.message);
					}
				}
			});
		}
	}
	public getEditAdminCompanyDetailsAemLabels() 
	{
		this.translate.get(TranslationKeys.editAdminCompanyDetailsLabels).subscribe({
			next: (response: EditAdminCompanyDetailsAemLabels) => 
			{
				if (response) 
					this.mapEditAdminCompanyDetailsLabels(response);					
			}
		});
	}
	mapEditAdminCompanyDetailsLabels(response: EditAdminCompanyDetailsAemLabels) 
	{
		this.editAdminCompanyDetailsAemLabelsState.heading = response.heading;
		this.editAdminCompanyDetailsAemLabelsState.pageLabels.companyName.label = response.pageLabels.companyName.label;
		this.editAdminCompanyDetailsAemLabelsState.pageLabels.addressLine1.label = response.pageLabels.addressLine1.label;
		this.editAdminCompanyDetailsAemLabelsState.pageLabels.addressLine2.label = response.pageLabels.addressLine2.label;
		this.editAdminCompanyDetailsAemLabelsState.pageLabels.country.label = response.pageLabels.country.label;
		this.editAdminCompanyDetailsAemLabelsState.pageLabels.state.label = response.pageLabels.state.label;
		this.editAdminCompanyDetailsAemLabelsState.pageLabels.city.label = response.pageLabels.city.label;
		this.editAdminCompanyDetailsAemLabelsState.pageLabels.zipCode.label = response.pageLabels.zipCode.label;
		this.editAdminCompanyDetailsAemLabelsState.errorText.requiredErrorMessage = response.errorText.requiredErrorMessage;
		this.editAdminCompanyDetailsAemLabelsState.errorText.minLengthZipCodeError = response.errorText.minLengthZipCodeError;
		this.editAdminCompanyDetailsAemLabelsState.errorText.canadaPostalCodeFormatError = response.errorText.canadaPostalCodeFormatError;
		this.editAdminCompanyDetailsModalState.buttons[0].label = response.pageButtons[0].cancel.label;
		this.editAdminCompanyDetailsModalState.buttons[1].label = response.pageButtons[0].save.label;
	}
	public patchEditDetailsForm(data: BusinessAccountsGridData): void 
	{
		this.buildEditDetailsForm.patchValue({
			accountName: data.companyDetails.companyName,
			addressLine1: data.companyDetails.addressLine1,
			addressLine2: data.companyDetails.addressLine2,
			country: this.sharedService.checkCountryCode(data.companyDetails.country),
			city: data.companyDetails.city,
			state: data.companyDetails.state,
			postalCode: this.sharedUtilService.formatZipCode(data.companyDetails.zipCode, data.companyDetails.country)
		});
	}
	formatZipCode(value: string) 
	{
		const countryValue = this.buildEditDetailsForm.controls['country'].value;
		this.buildEditDetailsForm.patchValue({ postalCode: this.sharedUtilService.formatZipCode(value, countryValue) });
	}
	public downloadLocationList(tempUserID: string) 
	{
		const selectedRowIndex: number = this.state.sectionMiddle.tableState.gridData.findIndex(row => row.id === tempUserID);
		const fileName: string = this.businessAccountData[selectedRowIndex].pendingLocationDetails.updatedFileName;
		this.businessAccountUtilService.locationLinkStatusChecked(true, selectedRowIndex, this.state);
		this.apiService.downloadLocationList(tempUserID).subscribe(
			{
				next: (res) => 
				{
					if (res) 
					{
						this.sharedService.downloadExcel(fileName, res);
						this.businessAccountUtilService.buttonStatusChecked(false, selectedRowIndex, this.state);
						this.businessAccountUtilService.locationLinkStatusChecked(false, selectedRowIndex, this.state);
					}
				},
				error: (error): void => 
				{
					if (error) 
						this.businessAccountUtilService.handleError(error, selectedRowIndex, this.state);
				}
			}
		);
	}
	updateAdminCompanyDetails(payload: AdminCompanyDetails) 
	{
		this.state.sectionMiddle.loading = true;
		this.apiService.updateAdminCompanyDetails(payload).subscribe({
			next: (response: ApiResponse) => 
			{
				if (response?.success && response?.statusCode === 200) 
				{
					this.updateAdminDetailsErrorMessage = '';
					this.isAdminDetailsUpdated$.next(true);
					this.toaster.success(response.message);
					this.getBusinessAccountRequestGridData();
				}
			},
			error: (error: HttpErrorResponse) => 
			{
				this.updateAdminDetailsErrorMessage = error.error.message;
				this.state.sectionMiddle.loading = false;
			},
			complete: () => 
			{
				setTimeout(() => 
				{
					this.state.sectionMiddle.loading = false;
				}, 1000);
			}
		});
	}
	public approveBusinessAdmin(payload: ApproveBusinessAdminPayload)
	{
		this.state.sectionMiddle.loading = true;
		this.apiService.approveBusinessAdminAccount(payload).subscribe({
			next: (response: ApiResponse) =>
			{
				if (response?.success && (response?.statusCode === 201 || response?.statusCode === 202))
				{
					this.state.sectionMiddle.loading = false;
					this.toaster.success(response.message);
					this.getBusinessAccountRequestGridData();
				}
			},
			error: (error: HttpErrorResponse): void =>
			{
				if (error)
				{
					this.state.sectionMiddle.loading = false;
					if (error.error.statusCode === 400 || error.error.statusCode === 500)
					{
						this.toaster.error(error.error.message);
					}
					this.loggerService.error(error.error);
				}
			},
			complete: () => 
			{
				setTimeout(() => 
				{
					this.state.sectionMiddle.loading = false;
				}, 1000);
			}
		});
	}
}
